/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { CodeEditorService, FilePath, FilePathType, FocusMode, SelectionRange, SourceFile, SourceFileView } from "@sandbox/code-editor";
import { packageService } from "services/package-service/PackageService";
import { getOptionalFiles } from "services/sandbox-api/transforms";
import { IModelInfo } from "services/SandboxIModels";
import { CodeShare, CodeShareMetadata, CodeShareModule, OptionalFileSet, ResolvedPackage } from "types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { store } from "../../reduxStore";
import { ImportMap } from "@sandbox/transpiler";

interface SandboxState {
  metadata?: CodeShareMetadata;
  sourceFiles: SourceFile[];
  changedFiles: SourceFile[];
  optionalFiles: OptionalFileSet[];
  dependencies?: CodeShareModule[];
  resolvedPackages?: ResolvedPackage[];
  importmap?: ImportMap
  itwinjsVersion?: string;
  editor: {
    openFiles: SourceFileView[];
    browsingStack: FilePath[];
  },
  allowedImodels?: IModelInfo[];
  selectedImodel?: IModelInfo;
}

const initialState: SandboxState = {
  sourceFiles: [],
  changedFiles: [],
  optionalFiles: [],
  editor: {
    openFiles: [],
    browsingStack: []
  }
};

export interface SandboxFileAction {
  filePath: FilePath,
  type: FilePathType,
  readOnly?: boolean,
  content?: string,
}

export interface OpenFileAction {
  filePath: FilePath,
  selection?: SelectionRange,
  focusMode?: FocusMode;
  readOnly?: boolean;
  filePathType?: FilePathType;
  alwaysVisible?: boolean;
}

export const getFilePath = (file: SourceFile) => file.filePathRenamed ?? file.filePath;

export const sandboxSlice = createSlice({
  name: "activeSandbox",
  initialState,
  reducers: {
    sandboxOpened: (state, { payload: sandbox }: PayloadAction<CodeShare>) => {
      const { files, optionalFiles, modules, allowedImodels, ...metadata } = sandbox;

      state.metadata = metadata;
      state.sourceFiles = files;
      state.dependencies = modules;
      state.optionalFiles = optionalFiles;
      state.allowedImodels = allowedImodels;
      state.selectedImodel = allowedImodels && allowedImodels.length ? allowedImodels[0] : undefined
      state.editor = {
        openFiles: [],
        browsingStack: [],
      }

      const allfiles: SourceFile[] = [...files, ...getOptionalFiles(optionalFiles)]
      for (const file of allfiles) {
        CodeEditorService.createFileModel(
          file.filePath,
          file.content ?? "",
          (filePath, content) => { store.dispatch(updateSandboxFile({ filePath, content, type: "file" })) }
        );
      }
    },
    closeSandbox: (state) => {
      state.metadata = undefined;
      state.sourceFiles = [];
      state.changedFiles = [];
      state.dependencies = undefined;
      state.optionalFiles = [];
      state.editor.browsingStack = [];
      state.editor.openFiles = [];
      state.resolvedPackages = undefined;
      state.itwinjsVersion = undefined;
      state.allowedImodels = undefined;
      state.selectedImodel = undefined;
      state.importmap = undefined;

      CodeEditorService.removeModels();
    },
    addSandboxFiles: ({ sourceFiles, changedFiles }, { payload: { filePath, type, content, readOnly } }: PayloadAction<SandboxFileAction>) => {
      if (!sourceFiles || !changedFiles) {
        console.warn("No active sandbox in store. State not changed");
        return;
      }
      const item = {
        filePath,
        type,
        readOnly,
        content: type === "file" ? content ?? "" : undefined
      };

      const index1 = sourceFiles.findIndex((sourceFile) => getFilePath(sourceFile) === filePath && sourceFile.deleted);
      if (index1 != -1)
        sourceFiles.splice(index1, 1);

      const index2 = changedFiles.findIndex((sourceFile) => getFilePath(sourceFile) === filePath && sourceFile.deleted);
      if (index2 != -1)
        changedFiles.splice(index2, 1);

      sourceFiles.push(item);
      changedFiles.push(item);

      if (type === "file")
        CodeEditorService.createFileModel(filePath, content ?? "", (filePath, content) => { store.dispatch(updateSandboxFile({ filePath, content, type: "file" })) });
    },
    deleteSandboxFiles: ({ sourceFiles, changedFiles, editor }, { payload: filePath }: PayloadAction<FilePath[]>) => {

      CodeEditorService.removeModels(filePath);

      filePath.forEach((path) => {
        const file = sourceFiles.find((sourceFile) => getFilePath(sourceFile) === path);
        if (file)
          file.deleted = true;
        const changedFile = changedFiles.find((sourceFile) => getFilePath(sourceFile) === path);
        if (changedFile)
          changedFile.deleted = true;
      });

      // remove from editor tabs
      editor.browsingStack = editor.browsingStack.filter((item) => !filePath.includes(item));
      editor.openFiles = editor.openFiles.filter((item) => !filePath.includes(getFilePath(item)));

      // restore previously viewed from browsingStack, if deleted file was active in code editor
      if (editor.browsingStack.length && editor.openFiles.length && editor.openFiles.every((item) => !item.active)) {
        const openedfile = editor.openFiles.find((item) => getFilePath(item) === editor.browsingStack[editor.browsingStack.length - 1]) || editor.openFiles[0];
        openedfile.active = true;
      }
    },
    renameSandboxFiles: ({ sourceFiles, changedFiles, editor }, { payload }: PayloadAction<FilePath[][]>) => {

      for (const [currentFilePath, newFilePath] of payload) {
        const file = sourceFiles.find((sourceFile) => getFilePath(sourceFile) === currentFilePath);
        if (file)
          file.filePathRenamed = newFilePath;

        const changedFile = changedFiles.find((changedFile) => getFilePath(changedFile) === currentFilePath);
        if (changedFile)
          changedFile.filePathRenamed = newFilePath;

        const opened = editor.openFiles.find((item) => getFilePath(item) === currentFilePath);

        if (opened && (changedFile ?? file) !== undefined) {
          opened.filePathRenamed = newFilePath;
          editor.browsingStack = editor.browsingStack.map((item) => item === currentFilePath ? newFilePath : item);
        }

        CodeEditorService.removeModels([currentFilePath]);
        CodeEditorService.createFileModel(newFilePath, (changedFile ?? file)!.content ?? "", (filePath, content) => { store.dispatch(updateSandboxFile({ filePath, content, type: "file" })) });
      }
    },
    updateSandboxFile: ({ sourceFiles, changedFiles, editor }, { payload: { filePath, content } }: PayloadAction<SandboxFileAction>) => {

      const file = changedFiles.find((changedFile) => getFilePath(changedFile) === filePath);
      if (file) {
        file.dirty = true;
        file.content = content;
      } else {
        const sourceFile = sourceFiles.find((sourceFile) => getFilePath(sourceFile) === filePath);
        if (sourceFile) {
          changedFiles.push({
            ...sourceFile,
            dirty: true,
            content
          });
        } else {
          console.warn("Source file was not found");
        }
      }

      const openFile = editor.openFiles.find((item) => getFilePath(item) === filePath);
      if (openFile)
        openFile.dirty = true;
    },
    sandboxSaved: (state) => {
      const { sourceFiles, changedFiles, editor } = state;
      const files: SourceFile[] = [];

      for (let i = 0; i < sourceFiles.length; i++) {
        if (!sourceFiles[i].deleted) {
          const changed = changedFiles.find((changedFile) => getFilePath(changedFile) === getFilePath(sourceFiles[i]));
          if (changed) {
            const _changed = { ...changed };
            _changed.filePath = getFilePath(_changed);
            _changed.dirty = undefined;
            files.push(_changed);
          } else {
            files.push(sourceFiles[i])
          }
        }
      }

      state.sourceFiles = files;
      state.changedFiles = [];

      editor.openFiles.forEach((item) => item.dirty = false);
    },
    openFile: ({ editor: { openFiles, browsingStack }, sourceFiles, changedFiles, optionalFiles }, { payload: { filePath, selection, focusMode, readOnly, filePathType, alwaysVisible } }: PayloadAction<OpenFileAction>) => {
      const file = [...changedFiles, ...sourceFiles, ...getOptionalFiles(optionalFiles)].find((item) => getFilePath(item) === filePath);

      let isOpened = false;
      for (let i = 0; i < openFiles.length; i++) {
        if (getFilePath(openFiles[i]) === filePath)
          isOpened = true;

        openFiles[i].active = getFilePath(openFiles[i]) === filePath;
        openFiles[i].selection = selection;
        openFiles[i].focusMode = focusMode;
      }

      if (!isOpened) {
        openFiles.push({
          type: filePathType ?? "file",
          filePath,
          readOnly,
          ...(file ?? {}),
          active: true,
          selection,
          focusMode,
          alwaysVisible
        });
      }

      browsingStack.push(filePath);
    },
    setEntry: ({ sourceFiles, changedFiles }, { payload: filePath }: PayloadAction<FilePath>) => {
      for (let i = 0; i < sourceFiles.length; i++)
        sourceFiles[i].entry = getFilePath(sourceFiles[i]) === filePath ? true : undefined;

      for (let i = 0; i < changedFiles.length; i++)
        changedFiles[i].entry = getFilePath(changedFiles[i]) === filePath ? true : undefined;
    },
    closeFile: ({ editor, changedFiles }, { payload: filePath }: PayloadAction<FilePath>) => {

      // remove file being closed from history first
      editor.browsingStack = editor.browsingStack.filter((item) => item !== filePath);

      const index = editor.openFiles.findIndex((item) => getFilePath(item) === filePath);

      if (index === -1)
        return;

      // get latest changes of file contents
      if (editor.openFiles[index].dirty) {
        const file = changedFiles.find((changedFile) => getFilePath(changedFile) === filePath);
        if (file) {
          file.dirty = true;
          file.content = CodeEditorService.getFileModelValue(filePath);
        }
      }

      // remove file from open list
      editor.openFiles.splice(index, 1);

      // restore previously opened file
      if (editor.browsingStack.length && editor.openFiles.length && editor.openFiles.every((item) => !item.active)) {
        const openedfile = editor.openFiles.find((item) => getFilePath(item) === editor.browsingStack[editor.browsingStack.length - 1]) || editor.openFiles[0];
        openedfile.active = true;
      }
    },
    addResolvedPackages: (state, { payload }: PayloadAction<ResolvedPackage[]>) => {
      state.resolvedPackages = payload;
      state.itwinjsVersion = packageService.getItwinjsVersion(payload);
    },
    importmapAdded: (state, { payload }: PayloadAction<ImportMap>) => {
      state.importmap = payload;
    },
    imodelSelected: (state, { payload }: PayloadAction<IModelInfo>) => {
      state.selectedImodel = payload;
    },
    sandboxMetadataChanged: (state, { payload }: PayloadAction<CodeShareMetadata>) => {
      state.metadata = payload;
    },
    sandboxLikedStateChanged: (state, { payload }: PayloadAction<{ isLiked?: boolean, likes?: number }>) => {
      if (state.metadata) {
        state.metadata.isLiked = payload.isLiked;
        state.metadata.likes = payload.likes ?? 0;
      }
    },
    sandboxImodelsChanged: (state, { payload }: PayloadAction<IModelInfo[]>) => {
      state.allowedImodels = payload;
      if (payload.length === 0) {
        state.selectedImodel = undefined;
      }

      if (state.selectedImodel) {
        const inTheList = payload.find((imodel) => imodel.iTwinId === state.selectedImodel?.iTwinId && imodel.iModelId === state.selectedImodel?.iModelId);
        if (!inTheList) {
          state.selectedImodel = payload[0];
        }
      }

      if (payload.length > 0 && !state.selectedImodel) {
        state.selectedImodel = payload[0];
      }
    },
    sandboxOptionalFilesChanged: (state, { payload }: PayloadAction<OptionalFileSet[]>) => {
      state.optionalFiles = payload;
      for (const file of getOptionalFiles(payload)) {
        CodeEditorService.createFileModel(
          file.filePath,
          file.content ?? ""
        );
      }
    },
  },
  // extraReducers: (builder) => {
  //   builder.addCase(sandboxOpened.fulfilled, (state, action) => {
  //     const { files, optionalFiles, modules, allowedImodels, ...metadata } = action.payload;

  //     state.metadata = metadata;
  //     state.sourceFiles = files;
  //     state.dependencies = modules;
  //     state.optionalFiles = optionalFiles;
  //     state.allowedImodels = allowedImodels;
  //     state.editor = {
  //       openFiles: [],
  //       browsingStack: [],
  //     }

  //     for (const file of files) {
  //       CodeEditorService.createFileModel(
  //         file.filePath,
  //         file.content ?? "",
  //         (filePath, content) => { store.dispatch(updateSandboxFile({ filePath, content, type: "file" })) }
  //       );
  //     }
  //     for (const file of getOptionalFiles(optionalFiles)) {
  //       CodeEditorService.createFileModel(
  //         file.filePath,
  //         file.content ?? ""
  //       );
  //     }
  //   })
  // }
});

// Action creators are generated for each case reducer function
export const {
  sandboxOpened,
  closeSandbox,
  addSandboxFiles,
  deleteSandboxFiles,
  renameSandboxFiles,
  updateSandboxFile,
  sandboxSaved,
  openFile,
  setEntry,
  closeFile,
  addResolvedPackages,
  imodelSelected,
  sandboxMetadataChanged,
  sandboxImodelsChanged,
  sandboxOptionalFilesChanged,
  sandboxLikedStateChanged,
  importmapAdded,
} = sandboxSlice.actions;

export default sandboxSlice.reducer;